import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { TOOL_SLUGS } from './lib/toolRoutes';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CompaniesPage from './pages/CompaniesPage';
import AssessmentWizard from './pages/AssessmentWizard';
import ReportPage from './pages/ReportPage';
import PricingPage from './pages/PricingPage';
import ChatAdvisor from './pages/ChatAdvisor';
import AdminPage from './pages/AdminPage';
import PasswordResetPage from './pages/PasswordResetPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import StoresPage from './pages/StoresPage';
import ToolHistoryPage from './pages/ToolHistoryPage';
import ToolsHubPage from './pages/tools/ToolsHubPage';
import GeoCheckupTool from './pages/tools/GeoCheckupTool';
import CompetitorRadarTool from './pages/tools/CompetitorRadarTool';
import ReportAnalyticsTool from './pages/tools/ReportAnalyticsTool';
import PricingEngineTool from './pages/tools/PricingEngineTool';
import LegalPage from './pages/LegalPage';
import NotFoundPage from './pages/NotFoundPage';
import DemoReportPage from './pages/DemoReportPage';
import EIhracatConsultingPage from './pages/EIhracatConsultingPage';
import CookieBanner from './components/CookieBanner';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-[#52525B]">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.is_admin) return <Navigate to="/dashboard" />;
  
  return children;
};

// English landing page wrapper - forces EN language
const EnglishLandingWrapper = () => (
  <LanguageProvider forceLang="en">
    <LandingPage />
  </LanguageProvider>
);

// English pricing page wrapper
const EnglishPricingWrapper = () => (
  <LanguageProvider forceLang="en">
    <PricingPage />
  </LanguageProvider>
);

const EnglishBlogListWrapper = () => (
  <LanguageProvider forceLang="en"><BlogListPage /></LanguageProvider>
);
const EnglishBlogPostWrapper = () => (
  <LanguageProvider forceLang="en"><BlogPostPage /></LanguageProvider>
);

// Tool pages — wrap in forced language based on slug prefix (/araclar = tr, /tools = en).
const TrToolHub = () => (<LanguageProvider forceLang="tr"><ToolsHubPage /></LanguageProvider>);
const EnToolHub = () => (<LanguageProvider forceLang="en"><ToolsHubPage /></LanguageProvider>);
const TrGeo = () => (<LanguageProvider forceLang="tr"><GeoCheckupTool /></LanguageProvider>);
const EnGeo = () => (<LanguageProvider forceLang="en"><GeoCheckupTool /></LanguageProvider>);
const TrRakip = () => (<LanguageProvider forceLang="tr"><CompetitorRadarTool /></LanguageProvider>);
const EnRakip = () => (<LanguageProvider forceLang="en"><CompetitorRadarTool /></LanguageProvider>);
const TrRapor = () => (<LanguageProvider forceLang="tr"><ReportAnalyticsTool /></LanguageProvider>);
const EnRapor = () => (<LanguageProvider forceLang="en"><ReportAnalyticsTool /></LanguageProvider>);
const TrFiyat = () => (<LanguageProvider forceLang="tr"><PricingEngineTool /></LanguageProvider>);
const EnFiyat = () => (<LanguageProvider forceLang="en"><PricingEngineTool /></LanguageProvider>);

// Client-side redirect for legacy tool URLs. Emits canonical + noindex meta
// so even before the navigation fires, crawlers receive strong "use the new URL"
// signals (treated by Googlebot as equivalent to 301 since 2019, see GSC docs).
// For full HTTP 301 see /app/PRODUCTION_SETUP.md (nginx/ingress snippet).
const LegacyRedirect = ({ to }) => {
  const { search, hash } = useLocation();
  useEffect(() => {
    const origin = window.location.origin;
    const setLink = (rel) => {
      let el = document.head.querySelector(`link[rel="${rel}"][data-legacy-redirect="1"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('data-legacy-redirect', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('href', `${origin}${to}`);
      return el;
    };
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"][data-legacy-redirect="1"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('data-legacy-redirect', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setLink('canonical');
    setMeta('robots', 'noindex, follow');
    return () => {
      document.head.querySelectorAll('[data-legacy-redirect="1"]').forEach((n) => n.remove());
    };
  }, [to]);
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/en" element={<EnglishLandingWrapper />} />
            <Route path="/en/pricing" element={<EnglishPricingWrapper />} />
            <Route path="/en/blog" element={<EnglishBlogListWrapper />} />
            <Route path="/en/blog/:slug" element={<EnglishBlogPostWrapper />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/demo" element={<DemoReportPage />} />
            <Route path="/en/demo" element={<DemoReportPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/e-ihracat-danismanlik" element={<EIhracatConsultingPage />} />
            <Route path="/e-ihracat-danismanligi" element={<EIhracatConsultingPage />} />
            <Route path="/reset-password" element={<PasswordResetPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardPage /></ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute><CompaniesPage /></ProtectedRoute>
            } />
            <Route path="/stores" element={
              <ProtectedRoute><StoresPage /></ProtectedRoute>
            } />
            <Route path="/assessment/:companyId/:assessmentId" element={
              <ProtectedRoute><AssessmentWizard /></ProtectedRoute>
            } />
            <Route path="/report/:assessmentId" element={
              <ProtectedRoute><ReportPage /></ProtectedRoute>
            } />
            <Route path="/advisor" element={
              <ProtectedRoute><ChatAdvisor /></ProtectedRoute>
            } />
            <Route path="/araclar/gecmis" element={
              <ProtectedRoute><ToolHistoryPage /></ProtectedRoute>
            } />
            <Route path="/tools/history" element={
              <ProtectedRoute><ToolHistoryPage /></ProtectedRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute><AdminPage /></AdminRoute>
            } />
            <Route path="/tools" element={<EnToolHub />} />
            <Route path="/araclar" element={<TrToolHub />} />
            {/* Turkish (primary, SEO-optimized) */}
            <Route path={`/araclar/${TOOL_SLUGS.geo.tr}`} element={<TrGeo />} />
            <Route path={`/araclar/${TOOL_SLUGS.rakip.tr}`} element={<TrRakip />} />
            <Route path={`/araclar/${TOOL_SLUGS.rapor.tr}`} element={<TrRapor />} />
            <Route path={`/araclar/${TOOL_SLUGS.fiyat.tr}`} element={<TrFiyat />} />
            {/* English equivalents under /tools/* */}
            <Route path={`/tools/${TOOL_SLUGS.geo.en}`} element={<EnGeo />} />
            <Route path={`/tools/${TOOL_SLUGS.rakip.en}`} element={<EnRakip />} />
            <Route path={`/tools/${TOOL_SLUGS.rapor.en}`} element={<EnRapor />} />
            <Route path={`/tools/${TOOL_SLUGS.fiyat.en}`} element={<EnFiyat />} />
            {/* Legacy → Turkish primary (preserves backlinks & email URLs) */}
            <Route path="/tools/geo-checkup" element={<LegacyRedirect to={`/araclar/${TOOL_SLUGS.geo.tr}`} />} />
            <Route path="/tools/rakip-radar" element={<LegacyRedirect to={`/araclar/${TOOL_SLUGS.rakip.tr}`} />} />
            <Route path="/tools/rapor-analitik" element={<LegacyRedirect to={`/araclar/${TOOL_SLUGS.rapor.tr}`} />} />
            <Route path="/tools/fiyatlama" element={<LegacyRedirect to={`/araclar/${TOOL_SLUGS.fiyat.tr}`} />} />
            <Route path="/legal/kvkk" element={<LegalPage slug="kvkk" />} />
            <Route path="/legal/gizlilik" element={<LegalPage slug="gizlilik" />} />
            <Route path="/legal/terms" element={<LegalPage slug="terms" />} />
            <Route path="/legal/cookies" element={<LegalPage slug="cookies" />} />
            <Route path="/legal/mesafeli-satis" element={<LegalPage slug="mesafeli-satis" />} />
            <Route path="/legal/on-bilgilendirme" element={<LegalPage slug="on-bilgilendirme" />} />
            <Route path="/legal/iade-iptal" element={<LegalPage slug="iade-iptal" />} />
            <Route path="/hakkimizda" element={<LegalPage slug="hakkimizda" />} />
            <Route path="/iletisim" element={<LegalPage slug="iletisim" />} />
            <Route path="/about" element={<LegalPage slug="hakkimizda" />} />
            <Route path="/contact" element={<LegalPage slug="iletisim" />} />
            {/* Catch-all 404 — must be last */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <CookieBanner />
          <Toaster position="top-right" />
        </AuthProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
